import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { BrandChip } from '@/components/BrandChip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { allBrandsState, modelState, recentRecallJobsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import { describeMatchedRule, type MatchableRecall, matchModel } from '@rock/recalls';
import type { ModelId, Recall, RecallMatch, RecallVerdict } from '@rock/types';
import { type FC, useMemo } from 'react';
import { isRecallJobActive } from './recallJobs';
import { RECALL_VERDICT_META } from './recallStatus';
import { CodeTag, TypeDisagreementBadge, typeDisagreementLabel } from './recallTags';
import { useRecallJobRequest } from './useRecallJobRequest';

/**
 * What the recall reaches. Before approval: the Impact Preview (models the rules name, units and homes that
 * would be evaluated, recalculated on request with no AI), with each model's matching rule and strength
 * recomputed here with the same pure matcher the server uses — the Phase 1 exit check is reading these by
 * hand. After approval: the live `recall-matches` and every unit's verdict with its evidence.
 */
export const RecallMatchesTab: FC<{ recall: Recall; matches: RecallMatch[]; verdicts: RecallVerdict[] }> = ({
    recall,
    matches,
    verdicts,
}) => {
    const dryRun = recall.dryRun;
    // Once approved, the live matches below are what it reaches; a preview matters again with a re-process waiting.
    const previewable = recall.status !== 'approved' || !!recall.draftRules;
    const matchable: MatchableRecall = useMemo(
        () => ({
            brandIds: recall.draftRules ? (recall.draftBrandIds ?? recall.brandIds) : recall.brandIds,
            rules: recall.draftRules ?? recall.rules,
        }),
        [recall],
    );
    return (
        <div className="flex flex-col gap-4">
            <Card>
                <CardHeader className="flex flex-row items-start justify-between gap-4">
                    <div className="flex flex-col gap-1.5">
                        <CardTitle>Impact Preview</CardTitle>
                        <CardDescription>
                            {dryRun ? (
                                <>
                                    If approved now: {dryRun.modelIds.length} model(s), {dryRun.unitCount} unit(s) in{' '}
                                    {dryRun.homeCount} home(s) would get a verdict
                                    {dryRun.unmodeledUnitCount
                                        ? `, ${dryRun.unmodeledUnitCount} of them with no model number, by brand and type`
                                        : ''}
                                    . Calculated {formatRelative(dryRun.at)} under{' '}
                                    <CodeTag>{dryRun.rulesVersion}</CodeTag>
                                    {previewable && dryRun.at < recall.updatedAt ? ', before the latest edit.' : '.'}
                                </>
                            ) : (
                                'Not calculated yet.'
                            )}
                        </CardDescription>
                    </div>
                    {previewable && <RecalculateButton recall={recall} />}
                </CardHeader>
                {dryRun && dryRun.modelIds.length > 0 && (
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>Match</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {dryRun.modelIds.map((modelId) => (
                                    <ModelRow
                                        key={modelId}
                                        modelId={modelId}
                                        recall={matchable}
                                        live={matches.find((m) => m.modelId === modelId)}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                )}
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Verdicts</CardTitle>
                    <CardDescription>
                        {verdicts.length
                            ? `${verdicts.length} unit(s) evaluated across homes.`
                            : recall.status === 'approved'
                              ? 'No verdicts written yet.'
                              : 'Verdicts are written once the recall is approved.'}
                    </CardDescription>
                </CardHeader>
                {verdicts.length > 0 && (
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Home</TableHead>
                                    <TableHead>Verdict</TableHead>
                                    <TableHead>Needs</TableHead>
                                    <TableHead>Evidence</TableHead>
                                    <TableHead>Homeowner</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {verdicts.map((verdict) => (
                                    <VerdictRow key={`${verdict.homeId}/${verdict.id}`} verdict={verdict} />
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                )}
            </Card>
        </div>
    );
};

/** Recalculate the preview from the current rules: a free server job, shown as running until it lands. */
const RecalculateButton: FC<{ recall: Recall }> = ({ recall }) => {
    const { request, isRequesting, canRequest } = useRecallJobRequest();
    const jobs = useAsyncAtomValue(recentRecallJobsState, []);
    const running = jobs.some(
        (job) =>
            isRecallJobActive(job) &&
            job.request.kind === 'impact-preview' &&
            job.request.recallIds.includes(recall.id),
    );
    const handleRecalculate = () =>
        request(
            { kind: 'impact-preview', recallIds: [recall.id] },
            'Recalculate',
            (jobId) => `Job ${jobId} recounts the models, units and homes from the current rules.`,
        );
    return (
        <Button
            size="sm"
            variant="outline"
            onClick={() => void handleRecalculate()}
            disabled={!canRequest || isRequesting || running || !(recall.draftRules ?? recall.rules)}
        >
            {running ? 'Recalculating…' : 'Recalculate'}
        </Button>
    );
};

const ModelRow: FC<{ modelId: ModelId; recall: MatchableRecall; live: RecallMatch | undefined }> = ({
    modelId,
    recall,
    live,
}) => {
    const model = useAsyncAtomValue(useMemo(() => modelState(modelId), [modelId]));
    const brands = useAsyncAtomValue(allBrandsState, []);
    const brand = model?.brandId ? brands.find((b) => b.id === model.brandId) : undefined;
    const onRowClick = useRowClick();
    const match = useMemo(
        () =>
            live ??
            (model
                ? matchModel(recall, {
                      brandId: model.brandId,
                      modelCanonical: model.modelCanonical,
                      spellings: model.spellings,
                  })
                : undefined),
        [live, model, recall],
    );
    return (
        <TableRow
            className="cursor-pointer hover:bg-muted/50"
            onClick={onRowClick(`/products/models/${encodeURIComponent(modelId)}`)}
        >
            <TableCell className="font-mono">{model?.modelDisplay ?? modelId}</TableCell>
            <TableCell onClick={(event) => event.stopPropagation()}>
                {brand ? <BrandChip brand={brand} /> : <span className="text-muted-foreground">—</span>}
            </TableCell>
            <TableCell>
                {model?.typeId ? (
                    <EquipmentTypeBadge typeId={model.typeId} />
                ) : (
                    <span className="text-muted-foreground">—</span>
                )}
            </TableCell>
            <TableCell>
                {match ? (
                    <span className="flex items-center gap-1">
                        <Badge variant="outline">{match.strength}</Badge>
                        <TypeDisagreementBadge evidence={live} />
                        <span className="font-mono text-xs">{describeMatchedRule(match)}</span>
                        {!live && <span className="text-xs text-muted-foreground">(preview)</span>}
                    </span>
                ) : (
                    <span className="text-muted-foreground">{model ? 'no longer matches these rules' : '…'}</span>
                )}
            </TableCell>
        </TableRow>
    );
};

const VerdictRow: FC<{ verdict: RecallVerdict }> = ({ verdict }) => {
    const onRowClick = useRowClick();
    const meta = RECALL_VERDICT_META[verdict.verdict];
    return (
        <TableRow
            className="cursor-pointer hover:bg-muted/50"
            onClick={onRowClick(`/directory/homes/${verdict.homeId}/equipment`)}
        >
            <TableCell className="font-mono text-xs">{verdict.homeId}</TableCell>
            <TableCell>
                {verdict.withdrawn ? (
                    <Badge variant="outline" className="text-muted-foreground">
                        withdrawn ({verdict.withdrawn.reason})
                    </Badge>
                ) : (
                    <Badge variant={meta.variant} className={meta.className}>
                        {meta.label}
                    </Badge>
                )}
            </TableCell>
            {verdict.scope === 'equipment' ? (
                <>
                    <TableCell>{verdict.needs.join(', ') || '—'}</TableCell>
                    <TableCell className="text-xs">
                        {verdict.evidence.strength} · {describeMatchedRule(verdict.evidence)}
                        {typeDisagreementLabel(verdict.evidence) ? ` · ${typeDisagreementLabel(verdict.evidence)}` : ''}
                        {verdict.evidence.serialRule ? ` · serial ${verdict.evidence.serialRule.value}` : ''}
                        {verdict.modelId ? '' : ' · no model number'}
                    </TableCell>
                </>
            ) : (
                <>
                    <TableCell>—</TableCell>
                    <TableCell className="text-xs">
                        follows {verdict.topicIds.length ? verdict.topicIds.join(', ') : 'a topic'}
                    </TableCell>
                </>
            )}
            <TableCell className="text-xs text-muted-foreground">
                {verdict.resolution?.outcome ??
                    (verdict.acceptedAt ? 'says it applies' : verdict.notifiedAt ? 'notified' : '—')}
            </TableCell>
        </TableRow>
    );
};
